import type { OutboxOperation } from '$db/local/db';
import { newId, nowIso, estimatePayloadBytes } from '../repositories/common';

/**
 * Entidades que NUNCA coalescen.
 * reviewEvents es append-only, cada evento es inmutable.
 */
const NON_COALESCIBLE_ENTITY_TYPES = new Set<string>([
  'reviewEvent',
  'reviewEvents'
]);

export interface CoalesceResult {
  ops: OutboxOperation[];
  droppedCount: number;
  bytesBefore: number;
  bytesAfter: number;
}

/**
 * Algoritmo puro de coalescencia (M4.1).
 *
 * Reglas:
 *  - Solo ops con status 'pending' son candidatas.
 *  - Clave de agrupación: [userId + entityType + entityId].
 *  - reviewEvents se excluye siempre.
 *  - Fusión campo por campo con LWW por op.createdAt.
 *  - Si el op más reciente del grupo es 'delete', gana el delete.
 *  - Si es 'upsert', los upserts previos aportan al payload consolidado.
 *  - La op consolidada recibe un id y una idempotencyKey nuevos.
 */
export function coalesceOutbox(ops: OutboxOperation[]): CoalesceResult {
  const bytesBefore = ops.reduce((sum, op) => sum + (op.bytes ?? 0), 0);

  const pending: OutboxOperation[] = [];
  const others: OutboxOperation[] = [];

  for (const op of ops) {
    if (op.status === 'pending') pending.push(op);
    else others.push(op);
  }

  const groups = new Map<string, OutboxOperation[]>();
  const singles: OutboxOperation[] = [];

  for (const op of pending) {
    if (NON_COALESCIBLE_ENTITY_TYPES.has(op.entityType)) {
      singles.push(op);
      continue;
    }
    const key = `${op.userId}|${op.entityType}|${op.entityId}`;
    const arr = groups.get(key);
    if (arr) arr.push(op);
    else groups.set(key, [op]);
  }

  const result: OutboxOperation[] = [...singles];
  let droppedCount = 0;

  for (const group of groups.values()) {
    if (group.length === 1) {
      const only = group[0];
      if (only) result.push(only);
      continue;
    }
    result.push(mergeGroup(group));
    droppedCount += group.length - 1;
  }

  const finalOps = [...result, ...others];
  const bytesAfter = finalOps.reduce((sum, op) => sum + (op.bytes ?? 0), 0);

  return { ops: finalOps, droppedCount, bytesBefore, bytesAfter };
}

function mergeGroup(group: OutboxOperation[]): OutboxOperation {
  const sorted = [...group].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt)
  );
  const latest = sorted[sorted.length - 1];
  if (!latest) {
    throw new Error('mergeGroup called with empty group');
  }

  if (latest.op === 'delete') {
    return {
      ...latest,
      id: newId(),
      idempotencyKey: newId(),
      updatedAt: nowIso(),
      bytes: estimatePayloadBytes(latest.payload),
      attempts: 0,
      status: 'pending'
    };
  }

  const merged: Record<string, unknown> = {};
  for (const op of sorted) {
    if (op.op !== 'upsert') continue;
    const p = op.payload as Record<string, unknown> | null;
    if (p && typeof p === 'object') Object.assign(merged, p);
  }

  return {
    ...latest,
    id: newId(),
    idempotencyKey: newId(),
    payload: merged,
    updatedAt: nowIso(),
    bytes: estimatePayloadBytes(merged),
    attempts: 0,
    status: 'pending'
  };
}